import { todayKey } from "~/shared/lib/container-group-schedule";
import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import {
  ScheduleReportRow,
  WEEKDAYS,
  useScheduleReportRows,
} from "../model/use-schedule-report-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import {
  byNumber,
  compareIds,
  entityNumber,
  numberColumn,
  reportDate,
  textCell,
} from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type ScheduleExportRow = ScheduleReportRow & {
  isTotal?: boolean;
  /** Totals line only: containers serviced per day of week, indexed 0=Sunday. */
  dayTotals?: number[];
};

/** «Перечень графиков вывоза + МГ» (AIS 15.1.4): one row per schedule of the schedule table. */
export const SchedulesReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = useScheduleReportRows();
    // A dependency of the totals, so a schedule starting today joins them once the date turns.
    const today = todayKey();
    const { locationCell, customerCell, entityCards } = useReportHyperlinks();

    const columns: Column<ScheduleExportRow>[] = useMemo(() => {
      // A weekday cell holds the installed count on the days the schedule runs, else nothing.
      const dayCount = (row: ScheduleExportRow, dow: number) =>
        row.isTotal
          ? (row.dayTotals?.[dow] ?? "")
          : row.weekdays.includes(dow)
            ? row.numberOfContainers
            : "";

      return [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) =>
            row.isTotal ? "" : entityNumber("containerGroup", row.containerGroupId),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.containerGroupId, b.containerGroupId),
        },
        {
          id: "site",
          header: t(Namespace.Analytics, "columns.site"),
          accessor: (row) => textCell(row.pickupLocationName, row.isTotal),
          width: 200,
          sortable: true,
          cell: (info) =>
            locationCell(info.row.original.pickupLocationId, info.row.original.pickupLocationName),
        },
        {
          id: "client",
          header: t(Namespace.Analytics, "table.customer"),
          accessor: (row) => textCell(row.ownerOrganizationName, row.isTotal),
          width: 200,
          sortable: true,
          cell: (info) =>
            customerCell(
              info.row.original.ownerOrganizationId,
              info.row.original.ownerOrganizationName,
            ),
        },
        {
          id: "contract",
          header: t(Namespace.Analytics, "collection.contract"),
          accessor: (row) => textCell(row.contractDealNumber, row.isTotal),
          width: 140,
          sortable: true,
        },
        {
          id: "routeGroups",
          header: t(Namespace.Analytics, "schedules.routeGroups"),
          accessor: (row) => textCell(row.pickupLocationGroups, row.isTotal),
          width: 200,
          sortable: true,
        },
        {
          id: "wasteType",
          header: t(Namespace.Analytics, "schedules.wasteType"),
          accessor: (row) =>
            textCell(
              row.jobCategory && t(Namespace.Enum, row.jobCategory as TranslationKey),
              row.isTotal,
            ),
          width: 130,
          sortable: true,
        },
        {
          id: "containerType",
          header: t(Namespace.Analytics, "collection.containerType"),
          accessor: (row) => textCell(row.containerTypeName, row.isTotal),
          width: 160,
          sortable: true,
        },
        {
          id: "installed",
          header: t(Namespace.Analytics, "schedules.installed"),
          accessor: (row) => row.numberOfContainers,
          width: 120,
          sortable: true,
          sortFn: (a, b) => a.numberOfContainers - b.numberOfContainers,
        },
        {
          id: "scheduleType",
          header: t(Namespace.Analytics, "schedules.scheduleType"),
          accessor: (row) => textCell(row.jobTypeLabel, row.isTotal),
          width: 170,
          sortable: true,
        },
        {
          id: "serviceProvider",
          header: t(Namespace.Analytics, "schedules.serviceProvider"),
          accessor: (row) => textCell(row.serviceProviderName, row.isTotal),
          width: 200,
          sortable: true,
        },
        {
          id: "scheduleParams",
          header: t(Namespace.Analytics, "schedules.scheduleParams"),
          accessor: (row) => textCell(row.params, row.isTotal),
          width: 240,
          sortable: true,
        },
        {
          id: "period",
          header: t(Namespace.Analytics, "schedules.period"),
          accessor: (row) => textCell(row.period, row.isTotal),
          width: 190,
          sortable: true,
          // By start, then end; an open end sorts last.
          sortFn: (a, b) =>
            a.validFrom.localeCompare(b.validFrom) ||
            (a.validTo || "9999").localeCompare(b.validTo || "9999"),
        },
        ...WEEKDAYS.map(
          ({ id, dow, key }): Column<ScheduleExportRow> => ({
            id,
            header: t(Namespace.Analytics, key),
            accessor: (row) => dayCount(row, dow),
            width: 64,
            sortable: true,
            sortFn: (a, b) => Number(dayCount(a, dow) || 0) - Number(dayCount(b, dow) || 0),
          }),
        ),
        {
          id: "contractEnd",
          header: t(Namespace.Analytics, "schedules.contractEnd"),
          accessor: (row) => (row.isTotal ? "" : reportDate(row.contractEndAt)),
          width: 150,
          sortable: true,
          sortFn: byNumber((row) => row.contractEndAt?.getTime()),
        },
      ];
    }, [t, locationCell, customerCell]);

    const exportRows = useMemo<ScheduleExportRow[]>(() => {
      if (!rows.length) return rows;
      // A group with several schedules is several rows but one set of containers.
      const installedByGroup = new Map(
        rows.map((r) => [String(r.containerGroupId), r.numberOfContainers]),
      );
      // Containers served on each weekday, once per group: only schedules in effect today, and a
      // group with two of them on one weekday (say MSW and its cleaning) still counts once.
      const daysByGroup = new Map<string, Set<number>>();
      for (const r of rows) {
        if (r.validFrom && r.validFrom > today) continue;
        const key = String(r.containerGroupId);
        const days = daysByGroup.get(key) ?? new Set<number>();
        r.weekdaysEveryWeek.forEach((dow) => days.add(dow));
        daysByGroup.set(key, days);
      }
      const dayTotals = Array.from({ length: 7 }, (_, dow) => {
        let sum = 0;
        daysByGroup.forEach((days, key) => {
          if (days.has(dow)) sum += installedByGroup.get(key) ?? 0;
        });
        return sum;
      });
      return [
        ...rows,
        {
          isTotal: true,
          dayTotals,
          scheduleId: 0n,
          containerGroupId: 0n,
          pickupLocationId: 0n,
          pickupLocationName: "",
          ownerOrganizationId: "",
          ownerOrganizationName: "",
          contractDealNumber: "",
          contractEndAt: null,
          pickupLocationGroups: "",
          jobCategory: "",
          containerTypeName: "",
          numberOfContainers: [...installedByGroup.values()].reduce((sum, n) => sum + n, 0),
          jobType: "",
          jobTypeLabel: "",
          serviceProviderName: "",
          period: "",
          validFrom: "",
          validTo: "",
          params: "",
          weekdays: [],
          weekdaysEveryWeek: [],
        },
      ];
    }, [rows, today]);

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="schedules_report"
        reportName={t(Namespace.Analytics, "reports.schedules")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => String(row.scheduleId)}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
